import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiClient, ApiError } from "../api/client";
import { RERUN_VIEWER_VERSION, type AutomaticLabel, type OrganizationRole, type PlaybackResponse, type SessionRecord } from "../api/types";
import { AutomaticLabelsPanel } from "../components/AutomaticLabelsPanel";
import { ErrorState, LoadingState } from "../components/AsyncState";
import { RerunViewer } from "../components/RerunViewer";
import { StatusBadge } from "../components/StatusBadge";
import { Breadcrumbs, Modal } from "../components/WorkflowUI";
import { formatBytes, formatDate, outcomeLabel } from "../utils/format";

export function SessionDetailPage() {
  const { sessionId = "" } = useParams();
  const { organizationId = "", projectId = "" } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState<SessionRecord | null>(null);
  const [playback, setPlayback] = useState<PlaybackResponse | null>(null);
  const [automaticLabels, setAutomaticLabels] = useState<AutomaticLabel[]>([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [projectName, setProjectName] = useState("");
  const [organizationRole, setOrganizationRole] = useState<OrganizationRole>();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [current, project, projectSessions, organizations, labels] = await Promise.all([
        apiClient.getSession(sessionId),
        apiClient.getProject(projectId),
        apiClient.listProjectSessions(projectId),
        apiClient.listOrganizations(),
        apiClient.listAutomaticLabels(sessionId),
      ]);
      if (project.organization_id !== organizationId || !projectSessions.some((item) => item.session_id === sessionId)) throw new ApiError("录制不属于当前组织项目", 404);
      setSession(current);
      setProjectName(project.name);
      setOrganizationRole(organizations.find((item) => item.id === organizationId)?.role);
      setAutomaticLabels(labels);
      setPlayback(current.status === "READY" && current.rrd_available ? await apiClient.playback(sessionId) : null);
    } catch (reason) { setError(reason instanceof ApiError ? reason.message : "录制数据加载失败"); }
    finally { setLoading(false); }
  }, [organizationId, projectId, sessionId]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (session?.status !== "VERIFYING") return;
    const timer = window.setTimeout(() => void load(), 2000);
    return () => window.clearTimeout(timer);
  }, [session?.status, load]);

  async function removeSession() {
    setDeleting(true); setDeleteError("");
    try {
      await apiClient.deleteSession(sessionId, deleteConfirmation);
      navigate(`/organizations/${organizationId}/projects/${projectId}`, { replace: true });
    } catch (reason) {
      setDeleteError(reason instanceof ApiError ? reason.message : "录制删除失败，请稍后重试");
    } finally { setDeleting(false); }
  }

  if (loading) return <LoadingState label="正在加载录制数据…" />;
  if (error || !session) return <ErrorState message={error || "录制数据不存在"} retry={() => void load()} />;
  const canDelete = (session.status === "READY" || session.status === "FAILED")
    && (organizationRole === "owner" || organizationRole === "maintainer");

  return (
    <>
      <Breadcrumbs items={[{ label: "组织", to: "/organizations" }, { label: projectName || "项目", to: `/organizations/${organizationId}/projects/${projectId}` }, { label: "录制详情" }]} />
      <header className="detail-header"><div><p className="eyebrow">录制详情</p><h1>{session.name}</h1><code>{session.session_id}</code></div><div className="header-actions"><StatusBadge status={session.status} />{canDelete && <button className="button danger" type="button" onClick={() => { setDeleteConfirmation(""); setDeleteError(""); setDeleteOpen(true); }}>删除录制</button>}</div></header>
      {session.error && <ErrorState message={session.error} />}
      <dl className="metrics detail-metrics card"><div><dt>采集时间</dt><dd>{formatDate(session.started_at_ns)}</dd></div><div><dt>结果</dt><dd>{outcomeLabel(session.outcome)}</dd></div><div><dt>时长</dt><dd>{session.duration_s.toFixed(1)} 秒</dd></div><div><dt>自动标签</dt><dd>{session.automatic_label_count} 条</dd></div><div><dt>RRD</dt><dd>{session.rrd_available ? formatBytes(session.rrd_size_bytes) : "未生成"}</dd></div></dl>
      <AutomaticLabelsPanel labels={automaticLabels} />
      <section className="viewer-section"><div className="section-title"><div><p className="eyebrow">同步回放</p><h2>Rerun 回放</h2></div>{playback && <span>录制 {playback.rerun_version} · Viewer {playback.viewer_version || RERUN_VIEWER_VERSION}{playback.verification?.server_verified ? " · 服务端 SHA256 已验证" : ""}{playback.delivery?.mode === "cdn_signed_range" ? " · CDN Range" : ""}</span>}</div>
        {playback ? <RerunViewer rrdUrl={playback.rrd_url} mobileRrdUrl={playback.mobile?.rrd_url} rerunVersion={playback.rerun_version} viewerVersion={playback.viewer_version} /> : <div className="card"><p className="empty-note">{session.rrd_available ? "录制数据完成云端校验后才能回放。" : "本次录制未生成 RRD，自动标签仍可查看。"}</p></div>}
      </section>
      <details className="card manifest-details"><summary>查看原始清单</summary><pre>{JSON.stringify(session.manifest, null, 2)}</pre></details>
      <Modal title="删除录制" open={deleteOpen} onClose={() => { if (!deleting) setDeleteOpen(false); }} width="small"><div className="confirm-content"><p>此操作会删除原始录制文件和上传记录，且无法撤销。已被任务引用的录制不能删除。</p><label>输入录制 ID 以确认<input autoFocus value={deleteConfirmation} onChange={(event) => setDeleteConfirmation(event.target.value)} placeholder={session.session_id} /></label>{deleteError && <p className="inline-error" role="alert">{deleteError}</p>}<footer className="modal-actions"><button className="button ghost" type="button" disabled={deleting} onClick={() => setDeleteOpen(false)}>取消</button><button className="button danger" type="button" disabled={deleting || deleteConfirmation !== session.session_id} onClick={() => void removeSession()}>{deleting ? "正在删除…" : "永久删除"}</button></footer></div></Modal>
    </>
  );
}
